import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { fmtINR, fmtNum, fmtDate, fmtTime } from "@/utils/format";
import { Mini } from "./AdminDailyReports";
import { toast } from "sonner";

import { DEFAULT_PRODUCTS, productColor } from "@/lib/products";

export default function AdminDailyDetail() {
  const { date } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/admin/daily-reports/${date}`).then((r) => setD(r.data)); }, [date]);

  const pdf = () => downloadFile(`/export/daily/${date}.pdf`, `daily_report_${date}.pdf`).catch((e) => toast.error(e.message));

  if (!d) return <AdminLayout><div className="h-40 animate-pulse bg-slate-100 rounded-2xl" /></AdminLayout>;
  const t = d.totals;
  const FUELS = (d.products || DEFAULT_PRODUCTS).map((p, i) => ({ k: p.key, label: `${p.name} (${p.unit})`, color: productColor(i) }));

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="daily-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-3xl font-display font-bold">Day Report · {fmtDate(date)}</h1>
            <p className="text-sm text-slate-500">{t.shifts_count} completed shift(s) · all shifts combined</p>
          </div>
        </div>
        <Button onClick={pdf} data-testid="daily-detail-pdf-btn" className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">
          <Download className="w-4 h-4 mr-2" /> Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="daily-detail-collection">
        <Mini label="Total Sale" value={fmtINR(t.grand_sales)} blue />
        <Mini label="Paytm / UPI" value={fmtINR(t.paytm)} />
        <Mini label="Credit / Udhar" value={fmtINR(t.credit)} />
        <Mini label="Card" value={fmtINR(t.card)} />
        <Mini label="Expected Cash" value={fmtINR(t.cash)} green />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="daily-detail-reconciliation">
        <Mini label="Actual Cash Received" value={t.reconciled_count ? fmtINR(t.actual_cash) : "—"} sub={`${t.reconciled_count}/${t.shifts_count} shifts reconciled`} />
        <Mini label="Shortage (−) / Excess (+)" value={t.reconciled_count ? `${t.cash_diff > 0 ? "+" : ""}${fmtINR(t.cash_diff)}` : "—"} />
        <Mini label="Status" value={!t.reconciled_count ? "Pending" : t.cash_diff < 0 ? "Shortage" : t.cash_diff > 0 ? "Excess" : "Balanced"} />
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 font-display font-bold">Fuel summary</div>
        <table className="w-full text-sm" data-testid="daily-detail-fuel-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
            <tr><th className="py-2 px-3">Product</th><th className="py-2 px-3 text-right">Gross</th><th className="py-2 px-3 text-right">Testing</th><th className="py-2 px-3 text-right">Net Sold</th><th className="py-2 px-3 text-right">Amount</th></tr>
          </thead>
          <tbody className="font-mono-num">
            {FUELS.map((f) => (
              <tr key={f.k} className="border-t border-slate-100">
                <td className="py-2 px-3 font-sans font-medium flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: f.color }} />{f.label}</td>
                <td className="py-2 px-3 text-right">{fmtNum(t[`gross_${f.k}`])}</td>
                <td className="py-2 px-3 text-right">{fmtNum(t[`test_${f.k}`])}</td>
                <td className="py-2 px-3 text-right font-semibold">{fmtNum(t[`net_${f.k}`])}</td>
                <td className="py-2 px-3 text-right font-semibold">{fmtINR(t[`sales_${f.k}`])}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
              <td className="py-2 px-3 font-sans">Total</td>
              <td className="py-2 px-3 text-right">{fmtNum(t.gross_total_litres)}</td>
              <td className="py-2 px-3 text-right">{fmtNum(t.total_testing_litres)}</td>
              <td className="py-2 px-3 text-right">{fmtNum(t.net_total_litres)}</td>
              <td className="py-2 px-3 text-right text-[color:var(--bpcl-blue)]">{fmtINR(t.grand_sales)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 font-display font-bold">Shift-wise breakdown</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="daily-detail-shifts-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
              <tr>
                <th className="py-2 px-3">Shift</th><th className="py-2 px-3">Employee</th><th className="py-2 px-3">MPD</th><th className="py-2 px-3">Time</th>
                {FUELS.map((f) => <th key={f.k} className="py-2 px-3 text-right">{f.label}</th>)}
                <th className="py-2 px-3 text-right">Sale</th><th className="py-2 px-3 text-right">Paytm</th><th className="py-2 px-3 text-right">Credit</th><th className="py-2 px-3 text-right">Card</th><th className="py-2 px-3 text-right">Cash</th><th className="py-2 px-3 text-right">Slip</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {d.shifts.map((s) => {
                const c = sumColl(s);
                return (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-sans font-medium">Shift {s.shift_number}</td>
                    <td className="py-2 px-3 font-sans">{s.employee_name} <span className="text-slate-400">({s.employee_code})</span></td>
                    <td className="py-2 px-3 font-sans">{s.mpd}</td>
                    <td className="py-2 px-3 text-xs">{fmtTime(s.start_time)} → {fmtTime(s.end_time)}</td>
                    {FUELS.map((f) => <td key={f.k} className="py-2 px-3 text-right">{fmtNum(s.totals?.[`net_${f.k}`])}</td>)}
                    <td className="py-2 px-3 text-right font-semibold">{fmtINR(s.totals?.grand_sales)}</td>
                    <td className="py-2 px-3 text-right">{fmtINR(c.paytm)}</td>
                    <td className="py-2 px-3 text-right">{fmtINR(c.credit)}</td>
                    <td className="py-2 px-3 text-right">{fmtINR(c.card)}</td>
                    <td className="py-2 px-3 text-right text-emerald-700">{fmtINR(c.cash)}</td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => nav(`/shift/${s.id}/print`)} data-testid={`daily-slip-${s.id}`}>
                        <Printer className="w-3.5 h-3.5 mr-1" /> Slip
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  );
}

export function sumColl(s) {
  const out = { paytm: 0, credit: 0, card: 0, cash: 0 };
  for (const c of [s.collection, s.collection_mpd2]) {
    if (!c) continue;
    out.paytm += Number(c.paytm || 0); out.credit += Number(c.credit || 0);
    out.card += Number(c.card || 0); out.cash += Number(c.expected_cash || 0);
  }
  return out;
}
